<script setup lang="ts" generic="TData">
import type { SortingState, ColumnFiltersState, RowSelectionState } from '@tanstack/vue-table';

import {
  useVueTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
} from '@tanstack/vue-table';
import { computed, h, ref, watch } from 'vue';

import type { BaseTableProps, BaseTableEmits } from './types';

import { AppLoadMore } from '../load-more';
import { useColumnWidths } from './composables/use-column-widths';
import { muteEmptyCells } from './muted-cell';
import BaseTableBody from './parts/BaseTableBody.vue';
import BaseTableFooter from './parts/BaseTableFooter.vue';
import BaseTableHeader from './parts/BaseTableHeader.vue';
import BaseTablePagination from './parts/BaseTablePagination.vue';

const props = withDefaults(defineProps<BaseTableProps<TData>>(), {
  loading: false,
  sortable: false,
  filterable: false,
  selectable: false,
  pagination: false,
  sticky: false,
  striped: false,
  rowClickable: false,
  nowrap: false,
  pageSize: 10,
  pageIndex: 0,
  serverSide: false,
  hasMore: false,
  fetchingMore: false,
  fixedColumnWidths: true,
});

const emit = defineEmits<BaseTableEmits<TData>>();

// Internal state
const internalSorting = ref<SortingState>(props.sorting || []);
const internalFiltering = ref<ColumnFiltersState>(props.filtering || []);
const internalSelection = ref<RowSelectionState>(props.selection || {});
const internalGlobalFilter = ref<string>(props.globalFilter || '');
const internalPagination = ref({
  pageIndex: props.pageIndex || 0,
  pageSize: props.pageSize || 10,
});

// Watch for external changes (props → internal state)
watch(
  () => props.sorting,
  (newSorting: SortingState | undefined) => {
    if (newSorting) {
      internalSorting.value = newSorting;
    }
  },
);

watch(
  () => props.filtering,
  (newFiltering: ColumnFiltersState | undefined) => {
    if (newFiltering) {
      internalFiltering.value = newFiltering;
    }
  },
);

watch(
  () => props.selection,
  (newSelection: RowSelectionState | undefined) => {
    if (newSelection) {
      internalSelection.value = newSelection;
    }
  },
);

watch(
  () => props.globalFilter,
  (newGlobalFilter: string | undefined) => {
    if (newGlobalFilter !== undefined) {
      internalGlobalFilter.value = newGlobalFilter;
    }
  },
);

watch(
  () => [props.pageIndex, props.pageSize] as const,
  ([newPageIndex, newPageSize]) => {
    internalPagination.value.pageIndex = newPageIndex;
    internalPagination.value.pageSize = newPageSize;
  },
);

// Emit changes (internal state → parent)
watch(internalSorting, (newSorting: SortingState) => {
  emit('update:sorting', newSorting);
});

watch(internalFiltering, (newFiltering: ColumnFiltersState) => {
  emit('update:filtering', newFiltering);
});

watch(internalSelection, (newSelection: RowSelectionState) => {
  emit('update:selection', newSelection);
});

watch(internalGlobalFilter, (newGlobalFilter: string) => {
  emit('update:globalFilter', newGlobalFilter);
});

watch(
  () => internalPagination.value.pageIndex,
  (newPageIndex: number) => {
    emit('update:pageIndex', newPageIndex);
  },
);

watch(
  () => internalPagination.value.pageSize,
  (newPageSize: number) => {
    emit('update:pageSize', newPageSize);
  },
);

// Prepare columns with selection checkbox if needed
const columnsWithSelection = computed(() => {
  const columns = muteEmptyCells(props.columns);
  if (!props.selectable) {
    return columns;
  }

  // Add selection column
  return [
    {
      id: 'select',
      header: ({ table }) => {
        return {
          template: () => {
            return h('input', {
              type: 'checkbox',
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
              class: 'base-table__checkbox',
            });
          },
        };
      },
      cell: ({ row }) => {
        return {
          template: () => {
            return h('input', {
              type: 'checkbox',
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              onChange: row.getToggleSelectedHandler(),
              class: 'base-table__checkbox',
            });
          },
        };
      },
    },
    ...columns,
  ];
});

// Create table instance
const table = useVueTable({
  get data() {
    return props.data;
  },
  get columns() {
    return columnsWithSelection.value;
  },
  getCoreRowModel: getCoreRowModel(),
  getRowId: props.getRowId,
  getSortedRowModel: props.sortable && !props.serverSide ? getSortedRowModel() : undefined,
  getFilteredRowModel: props.filterable && !props.serverSide ? getFilteredRowModel() : undefined,
  getPaginationRowModel: props.pagination && !props.serverSide ? getPaginationRowModel() : undefined,
  state: {
    get sorting() {
      return props.sortable ? internalSorting.value : undefined;
    },
    get columnFilters() {
      return props.filterable ? internalFiltering.value : undefined;
    },
    get rowSelection() {
      // Переданное selection подсвечивает строку и без колонки чекбоксов.
      return props.selectable || props.selection ? internalSelection.value : undefined;
    },
    get globalFilter() {
      return props.filterable ? internalGlobalFilter.value : undefined;
    },
    get pagination() {
      return props.pagination ? internalPagination.value : undefined;
    },
  },
  onSortingChange: (updaterOrValue) => {
    if (props.sortable) {
      internalSorting.value =
        typeof updaterOrValue === 'function' ? updaterOrValue(internalSorting.value) : updaterOrValue;
    }
  },
  onColumnFiltersChange: (updaterOrValue) => {
    if (props.filterable) {
      internalFiltering.value =
        typeof updaterOrValue === 'function' ? updaterOrValue(internalFiltering.value) : updaterOrValue;
    }
  },
  onRowSelectionChange: (updaterOrValue) => {
    if (props.selectable) {
      internalSelection.value =
        typeof updaterOrValue === 'function' ? updaterOrValue(internalSelection.value) : updaterOrValue;
    }
  },
  onGlobalFilterChange: (updaterOrValue) => {
    if (props.filterable) {
      internalGlobalFilter.value =
        typeof updaterOrValue === 'function' ? updaterOrValue(internalGlobalFilter.value) : updaterOrValue;
    }
  },
  onPaginationChange: (updaterOrValue) => {
    if (props.pagination) {
      internalPagination.value =
        typeof updaterOrValue === 'function' ? updaterOrValue(internalPagination.value) : updaterOrValue;
    }
  },
  enableSorting: props.sortable,
  enableFilters: props.filterable,
  enableRowSelection: props.selectable,
  enableGlobalFilter: props.filterable,
  manualSorting: props.serverSide,
  manualFiltering: props.serverSide,
  manualPagination: props.serverSide || props.pageCount !== undefined,
  pageCount: props.pageCount,
});

// Composable для управления шириной колонок
const { tableRef, getColumnStyle, isWidthsCalculated } = useColumnWidths({
  fixedColumnWidths: props.fixedColumnWidths,
  data: () => props.data,
  table,
});

// tableRef используется в template через ref="tableRef"
void tableRef.value;

// Computed
const isEmpty = computed(() => {
  return !props.loading && props.data.length === 0;
});

const emptyState = computed(() => {
  if (typeof props.empty === 'string') {
    return { title: props.empty };
  }
  return props.empty || { title: 'Нет данных' };
});

const stickyClass = computed(() => {
  if (props.sticky === true) {
    return 'base-table--sticky';
  }
  if (props.sticky === 'header') {
    return 'base-table--sticky-header';
  }
  if (props.sticky === 'footer') {
    return 'base-table--sticky-footer';
  }
  return '';
});
</script>

<template>
  <div
    :class="[
      'base-table-container',
      {
        'base-table-container--loading': loading,
      },
    ]"
  >
    <!-- Table -->
    <div class="base-table-wrapper">
      <table
        ref="tableRef"
        :class="[
          'base-table',
          stickyClass,
          {
            'base-table--striped': striped,
            'whitespace-nowrap': nowrap,
          },
        ]"
        :style="fixedColumnWidths && isWidthsCalculated ? { tableLayout: 'fixed' } : undefined"
      >
        <!-- Header -->
        <BaseTableHeader
          :header-groups="table.getHeaderGroups()"
          :sortable="sortable"
          :get-column-style="getColumnStyle"
        >
          <template
            v-for="(_, name) in $slots"
            #[name]="slotProps"
          >
            <slot
              :name="name"
              v-bind="slotProps"
            />
          </template>
        </BaseTableHeader>

        <!-- Body -->
        <BaseTableBody
          :loading="loading"
          :is-empty="isEmpty"
          :empty-state="emptyState"
          :rows="table.getRowModel().rows"
          :header-groups="table.getHeaderGroups()"
          :get-column-style="getColumnStyle"
          :server-side="serverSide"
          :has-more="hasMore"
          :fetching-more="fetchingMore"
          :row-clickable="rowClickable"
          :row-class="rowClass"
          :row-divider="rowDivider"
          @row-click="(row, event) => emit('row-click', row, event)"
          @row-hover="(row, event) => emit('row-hover', row, event)"
        >
          <template
            v-for="(_, name) in $slots"
            #[name]="slotProps"
          >
            <slot
              :name="name"
              v-bind="slotProps"
            />
          </template>
        </BaseTableBody>

        <!-- Footer -->
        <BaseTableFooter :footer-groups="table.getFooterGroups()" />
      </table>
    </div>

    <AppLoadMore
      v-if="serverSide && !loading && !isEmpty"
      :has-more="hasMore"
      :loading="fetchingMore"
      @load="emit('fetch-more')"
    />

    <!-- Pagination -->
    <BaseTablePagination
      v-if="pagination"
      :table="table"
      :loading="loading"
      :is-empty="isEmpty"
    />
  </div>
</template>

<style scoped>
@reference "../../../tailwind.css";

@layer components {
  /* Container */
  .base-table-container {
    @apply flex flex-col gap-4;
  }

  .base-table-wrapper {
    @apply relative overflow-auto rounded-lg;
    background-color: var(--surface-card);
  }

  /* Table */
  .base-table {
    /* min-w-full + width:auto: на mobile таблица растёт по естественной ширине колонок,
       wrapper (overflow-auto) даёт горизонтальный скролл; на широких — растягивается до 100%. */
    @apply w-auto min-w-full border-collapse;
  }

  /* Общие ячейки заголовка и итогов внутренних частей таблицы. */
  :deep(.base-table__th) {
    @apply px-4 py-3 text-left text-sm font-semibold;
    color: var(--text-primary);
  }

  /* Шапка тише строк: глаз идёт к содержимому, а не к подписям колонок. */
  :deep(.base-table__thead .base-table__th) {
    @apply text-xs font-normal;
    color: var(--text-secondary);
  }

  /* Striped */
  .base-table--striped :deep(.base-table__row:nth-child(even)) {
    background-color: color-mix(in srgb, var(--text-primary) 2%, transparent);
  }

  /* Sticky */
  .base-table--sticky :deep(.base-table__thead),
  .base-table--sticky-header :deep(.base-table__thead) {
    @apply sticky top-0 z-10;
    background-color: var(--surface-card);
  }

  .base-table--sticky :deep(.base-table__tfoot),
  .base-table--sticky-footer :deep(.base-table__tfoot) {
    @apply sticky bottom-0 z-10;
    background-color: var(--surface-raised);
  }

  /* Checkbox */
  .base-table__checkbox {
    @apply h-4 w-4 cursor-pointer rounded border;
    border-color: var(--border);
    accent-color: var(--accent);
  }
}
</style>
